import { createSignal } from "solid-js";
import { useApplication } from "@/context/app";

export default function usePrescriptionPrint() {
  const [loading, setLoading] = createSignal(false);
  const { successToast, errorToast } = useApplication();

  const generatePrescriptionHTML = (prescription) => {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatTime = (timeString) => {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Medical Prescription</title>
    <style>
        body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 20px;
            font-size: 12px;
            line-height: 1.4;
        }
        .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
            margin-bottom: 20px;
        }
        .clinic-name {
            font-size: 24px;
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 5px;
        }
        .clinic-info {
            font-size: 14px;
            color: #7f8c8d;
        }
        .prescription-id {
            text-align: right;
            font-size: 12px;
            color: #7f8c8d;
            margin-bottom: 20px;
        }
        .section {
            margin-bottom: 20px;
        }
        .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #2c3e50;
            border-bottom: 1px solid #bdc3c7;
            padding-bottom: 5px;
            margin-bottom: 10px;
        }
        .patient-info, .doctor-info {
            display: flex;
            justify-content: space-between;
        }
        .info-group {
            flex: 1;
        }
        .info-label {
            font-weight: bold;
            color: #34495e;
        }
        .info-value {
            color: #2c3e50;
        }
        .diagnoses-list, .therapies-list, .sessions-list, .complaints-list, .tests-list {
            margin-left: 0;
        }
        .list-item {
            margin-bottom: 5px;
            padding: 5px;
            background-color: #f8f9fa;
            border-left: 3px solid #3498db;
        }
        .complaint-item {
            margin-bottom: 5px;
            padding: 0;
            background-color: transparent;
        }
        .item-name {
            font-weight: bold;
            color: #2c3e50;
            font-size: 11px;
        }
        .test-item-name {
            font-weight: bold;
            color: #2c3e50;
            font-size: 12px;
        }
        .sub-heading {
            font-size: 12px;
            font-weight: bold;
            color: #34495e;
            margin-bottom: 8px;
            margin-top: 10px;
        }
        .item-details {
            color: #7f8c8d;
            font-size: 11px;
            margin-top: 2px;
        }
        .notes-section {
            background-color: #f8f9fa;
            padding: 10px;
            border-radius: 5px;
            border-left: 3px solid #e74c3c;
        }
        .footer {
            margin-top: 30px;
            text-align: center;
            border-top: 1px solid #bdc3c7;
            padding-top: 10px;
            font-size: 11px;
            color: #7f8c8d;
        }
        .signature-line {
            margin-top: 40px;
            border-top: 1px solid #333;
            width: 200px;
            text-align: center;
            padding-top: 5px;
        }
        @media print {
            body { margin: 0; }
            .no-print { display: none; }
        }
        .prescription-table {
            display: flex;
            width: 100%;
        }
        .col-1 {
            width: 25%;
            padding-right: 10px;
            box-sizing: border-box;
        }
        .col-2 {
            width: 50%;
            padding: 0 10px;
            box-sizing: border-box;
        }
        .col-3 {
            width: 25%;
            padding-left: 10px;
            box-sizing: border-box;
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="clinic-name">Speech and Mind Hub</div>
        <div class="clinic-info">Helping Minds Dind Their Voice</div>
        <div class="clinic-info">Back Side of Art Council Multan Near Multan City Lab</div>
        <div class="clinic-info">Phone: (0300) 8877793 | Email: contact@speechandmindhub.com</div>
    </div>

    <div class="section">
        <div class="section-title">Appointment Information</div>
        <div class="doctor-info">
            <div class="info-group">
                <div><span class="info-label">Doctor:</span> <span class="info-value">${prescription.doctor.name}</span></div>
            </div>
            <div class="info-group">
                <div><span class="info-label">Date:</span> <span class="info-value">${formatDate(prescription.appointment.date)}</span></div>
            </div>
        </div>
    </div>

    <div class="section">
        <div class="section-title">Patient Information</div>
        <div class="patient-info">
            <div class="info-group">
                <div><span class="info-label">Name:</span> <span class="info-value">${prescription.patient.name}</span></div>
                <div><span class="info-label">Gender:</span> <span class="info-value">${prescription.patient.gender || 'N/A'}</span></div>
                <div><span class="info-label">Age:</span> <span class="info-value">${prescription.patient.age || 'N/A'} years</span></div>
            </div>
        </div>
    </div>

    <div class="prescription-table">
    <table style="width: 100%; border-collapse: collapse;">
        <tr>
            <td style="width: 25%; padding-right: 10px; box-sizing: border-box; vertical-align: top;">
                <div class="section">
                    <div class="section-title">Presenting Complaints</div>
                    <div class="sub-heading">Parents' Brief</div>
                    <div class="complaints-list">
                        ${prescription.complaints.length > 0 ? `
                            ${prescription.complaints.map(complaint => `
                                <div class="complaint-item">
                                    <div class="item-name">${complaint.name}</div>
                                </div>
                            `).join('')}
                        ` : ''}
                    </div>
                </div>
            </td>
            <td style="width: 50%; padding: 0 10px; box-sizing: border-box; vertical-align: top;">
                <div class="section">
                    <div class="section-title">Rx</div>
                    <div class="sub-heading">History</div>
                    <div class="complaints-list">
                        ${prescription.histories && prescription.histories.length > 0 ? `
                            ${prescription.histories.map(history => `
                                <div class="complaint-item">
                                    <div class="item-name">${history.name} = ${history.value || 'N/A'}</div>
                                </div>
                            `).join('')}
                        ` : ''}
                    </div>
                </div>
                
            </td>
            <td style="width: 25%; padding-left: 10px; box-sizing: border-box; vertical-align: top;">
                <div class="section">
                    <div class="section-title">Developmental Profile</div>
                    <div class="sub-heading">Test for Diagnosis</div>
                    <div class="tests-list">
                        ${prescription.tests.length > 0 ? `
                            ${prescription.tests.map(test => `
                                <div class="complaint-item">
                                    <div class="test-item-name">${test.name}</div>
                                    ${test.results && Object.keys(test.results).length > 0 ? `
                                        <div style="margin-top: 4px; margin-left: 8px;">
                                            ${Object.entries(test.results).map(([option, value]) => `
                                                <div style="font-size: 10px; color: #7f8c8d; margin-bottom: 2px;">
                                                    ${option}: ${value} year(s)
                                                </div>
                                            `).join('')}
                                        </div>
                                    ` : ''}
                                </div>
                            `).join('')}
                        ` : ''}
                    </div>
                </div>
                
                <div class="section">
                    <div class="section-title">Diagnosis</div>
                    <div class="diagnoses-list">
                        ${prescription.diagnoses.length > 0 ? `
                            ${prescription.diagnoses.filter(diagnosis => diagnosis.status === 'new' || diagnosis.status === 'in progress').map(diagnosis => `
                                <div class="complaint-item">
                                    <div class="item-name">${diagnosis.name}</div>
                                </div>
                            `).join('')}
                        ` : ''}
                    </div>
                </div>
                
                <div class="section">
                    <div class="section-title">Management Plant</div>
                    <div class="therapies-list">
                        ${prescription.therapies.length > 0 ? `
                            ${prescription.therapies.filter(therapy => therapy.status === 'new' || therapy.status === 'in progress').map(therapy => `
                                <div class="complaint-item">
                                    <div class="item-name">${therapy.name}</div>
                                </div>
                            `).join('')}
                        ` : ''}
                    </div>
                    ${prescription.sessions.length > 0 ? `
                        <div class="sub-heading">Given Exercise plan for home/session</div>
                        <div class="sessions-list">
                            ${prescription.sessions.map(session => `
                                <div class="complaint-item">
                                    <div class="item-name">${session.name}</div>
                                </div>
                            `).join('')}
                        </div>
                    ` : ''}
                </div>
            </td>
    </tr>
    </table>
    </div>

    <div class="footer">
        <div class="signature-line">Doctor's Signature</div>
        <div style="margin-top: 20px;">
          <strong>Important:</strong> This prescription is valid for the date of issue only.<br>
          Please follow the doctor's instructions carefully and report any adverse reactions immediately. <br/>
          This prescription is not valid for any court of law.
        </div>
    </div>
</body>
</html>`;
  };

  const printPrescription = async (appointment) => {
    setLoading(true);
    try {
      // Fetch prescription data first

      // Generate HTML content
      const htmlContent = generatePrescriptionHTML(appointment);

      // Check if qz-tray is available
      if (typeof qz === 'undefined') {
        // Fallback to browser print
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
        successToast('Prescription opened in browser print dialog');
        return;
      }

      // Configure qz-tray
      qz.websocket.connect()
        .then(() => {
          // Find default printer
          return qz.printers.getDefault();
        })
        .then((defaultPrinter) => {
          // Configure print job
          const config = qz.configs.create(defaultPrinter, {
            scaleContent: true,
            rasterize: false,
            orientation: 'portrait',
            size: { width: '8.5in', height: '11in' },
            margins: { top: '0.5in', right: '0.5in', bottom: '0.5in', left: '0.5in' }
          });

          // Create print data
          const data = [{
            type: 'html',
            data: htmlContent,
            options: {
              width: '8.5in',
              height: '11in'
            }
          }];

          // Print
          return qz.print(config, data);
        })
        .then(() => {
          successToast('Prescription printed successfully');
          return qz.websocket.disconnect();
        })
        .catch((error) => {
          console.error('Print error:', error);
          // Fallback to browser print if qz-tray fails
          const printWindow = window.open('', '_blank');
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
          printWindow.close();
          successToast('Prescription opened in browser print dialog (QZ Tray failed)');
        });
    } catch (error) {
      console.error('Prescription error:', error);
      errorToast('Failed to generate prescription: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    printPrescription
  };
}